import { type FC, useState } from "react";

import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { sentryUtils } from "@/shared/utils/sentry";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import {
  LtcInsuranceForm,
  EMPTY_LTC_INSURANCE_FORM,
  buildMetadata,
  validateLtcInsuranceFields,
  type LtcInsuranceFormValues,
  type LtcInsuranceFieldErrors,
} from "./LtcInsuranceForm";

export const AddLtcInsurance: FC<FlowCustomComponentProps> = ({ goToScreen, answers }) => {
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const { assets, addItem, isAddingItem } = useEstateItems(estateId);
  const hasExistingPolicies = filterByCategory(assets, "Long-term care insurance").length > 0;

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    LtcInsuranceFormValues,
    LtcInsuranceFieldErrors
  >(EMPTY_LTC_INSURANCE_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (isSubmitting || !estateId) return;

    const errors = validateLtcInsuranceFields(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      let sourceDocumentId: number | undefined;

      if (form.documentFile) {
        sourceDocumentId = await uploadDocumentWithAuth(
          form.documentFile,
          estateId,
          "ltc_insurance_policy",
        );
      }

      await addItem({
        estate_id: estateId,
        category: "Long-term care insurance",
        classification: "asset",
        item_name: form.companyName.trim(),
        item_type: "Long-term care insurance",
        balance: parseFloat(form.dailyBenefitAmount) || 0,
        metadata: buildMetadata(form),
        source_document_id: sourceDocumentId,
      });

      goToScreen("ltc_insurance_manage", {
        ...answers,
        ltc_insurance_review: "found",
        add_ltc_insurance: "",
        ltc_insurance_manage: "yes",
      });
    } catch (err) {
      setError("Failed to add policy. Please try again.");
      sentryUtils.captureError(err, "AddLtcInsurance submission failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-center">
        {hasExistingPolicies
          ? "Add another LTC insurance policy"
          : "Add a long-term care insurance policy"}
      </h2>
      <p className="mt-2 text-center text-secondary">
        Share what you know. We'll help you gather details later.
      </p>
      <div className="mt-8 w-full">
        <AddItemLayout
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          error={error}
          disabled={isAddingItem}
        >
          <LtcInsuranceForm form={form} onChange={updateForm} fieldErrors={fieldErrors} />
        </AddItemLayout>
      </div>
    </div>
  );
};
